// pages/DmsProject.jsx — DMS Project (dms.pgocid.com, akun AWS Indomas)
// Tab: Request flow (diagram live dari ALB target health + status OpenSearch), Load balancer, OpenSearch.
// Read-only. Data: /api/ec2-health/alb & /api/ec2-health/opensearch (account=indomas). Tidak menyentuh instance DMS.
import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../context/AuthContext';
import Ec2AlbView from './Ec2AlbView';
import Ec2OpenSearchView from './Ec2OpenSearchView';

const ACCOUNT = 'indomas';
const LB_NAME = 'dms-pgoc';
const OS_DOMAIN = 'dms-pgoc';
const TABS = [['flow', 'Request flow'], ['alb', 'Load balancer'], ['opensearch', 'OpenSearch'], ['about', 'About Rockeye']];

// Summary of public info (rockeye.com, kb.rockeye.com/dms/faq), checked 2026-11-04.
const FEATURES = [
  ['Storage', 'Central repository with folders, tags and metadata. Upload up to 1 GB. Archive/purge; deleted documents go to a recovery area first.'],
  ['Search', 'Full-text search inside PDF, Word and Excel, plus metadata and file names. AI assistant for Q&A and summaries (text or voice).'],
  ['OCR', 'Printed / computer-generated text only. Handwriting is not supported.'],
  ['Versioning', 'Automatic version history on every update; compare, restore and add notes per version.'],
  ['Workflow', 'Configurable approval flow, role-based reviewers, digital signature, audit trail, task assignment within a department.'],
  ['Security', 'Read / Modify / Delete / Control rights per folder, MFA (Google Authenticator, email, device), expiring share links, download restrictions, activity log.'],
  ['Other', 'Edit documents in the browser without MS Office, mobile app, links to ERP / HRMS / procurement.'],
];
const MAPPING = [
  ['Web-1 / Web-2 (nginx + PHP CodeIgniter)', 'Web application: login, folders, upload, approval', 'confirmed'],
  ['RDS MySQL', 'Metadata, users, access rights, versions, workflow', 'likely'],
  ['S3 dms-pgoc-data', 'Original document files and their versions', 'likely'],
  ['OpenSearch dms-pgoc', 'Full-text index and AI search', 'likely'],
  ['Core-1 / Core-2 (coredms, m6i.4xlarge)', 'Heavy services: OCR, text extraction, indexing, AI, document editor/preview', 'assumption'],
];
const QUESTIONS = [
  'Which processes run synchronously inside PHP (upload, text extraction, calls to Core)?',
  'What is the timeout for Web → Core calls, and what happens when Core is slow?',
  'What are the recommended PHP-FPM settings (pm.max_children, request_terminate_timeout)?',
  'Is there a PHP health endpoint we can use for the ALB target group health check?',
];

function AboutTab() {
  const badge = { confirmed: 'bg-emerald-50 text-emerald-700', likely: 'bg-sky-50 text-sky-700', assumption: 'bg-amber-50 text-amber-700' };
  const Table = ({ head, rows }) => (
    <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
      <table className="w-full text-sm">
        <thead><tr className="bg-gray-50 text-left text-xs text-gray-500">{head.map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr></thead>
        <tbody>{rows}</tbody>
      </table>
    </div>
  );
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-gray-200 bg-white p-4 text-sm text-gray-600">
        <div className="mb-1 font-semibold text-gray-800">Vendor</div>
        ROCKEYE is an ERP and industrial software vendor (strong in oil &amp; gas and logistics). The DMS is one module of their ERP platform.
        Public material is mostly marketing; architecture and tech stack are not published.
      </div>
      <div className="text-sm font-semibold text-gray-800">Features (per vendor website / FAQ)</div>
      <Table head={['Area', 'Description']} rows={FEATURES.map(([a, b]) => (
        <tr key={a} className="border-t border-gray-100"><td className="px-3 py-2 font-medium text-gray-800">{a}</td><td className="px-3 py-2 text-gray-600">{b}</td></tr>
      ))} />
      <div className="text-sm font-semibold text-gray-800">Mapping to our AWS components</div>
      <Table head={['AWS component', 'Probable role in Rockeye DMS', 'Basis']} rows={MAPPING.map(([a, b, c]) => (
        <tr key={a} className="border-t border-gray-100"><td className="px-3 py-2 font-medium text-gray-800">{a}</td><td className="px-3 py-2 text-gray-600">{b}</td>
          <td className="px-3 py-2"><span className={`rounded px-1.5 py-0.5 text-xs font-medium ${badge[c]}`}>{c}</span></td></tr>
      ))} />
      <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-4 text-sm">
        <div className="mb-1 font-semibold text-gray-800">Link to the intermittent 504</div>
        <div className="text-gray-600">Large uploads (up to 1 GB), text extraction or slow Web → Core calls can hold PHP-FPM workers until the pool is full — matching the symptom on Web-1. Questions for the vendor:</div>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-gray-600">{QUESTIONS.map((q) => <li key={q}>{q}</li>)}</ul>
      </div>
      <div className="text-[11px] text-gray-400">
        Sources: <a className="underline" href="https://rockeye.com/document-management-system.html" target="_blank" rel="noreferrer">rockeye.com/document-management-system</a> ·{' '}
        <a className="underline" href="https://kb.rockeye.com/dms/faq" target="_blank" rel="noreferrer">kb.rockeye.com/dms/faq</a> (checked 2026-11-04). Role of Core nodes is an assumption — confirm with the vendor.
      </div>
    </div>
  );
}

// Instance yang dikenal di arsitektur DMS (nama tag EC2). IP/state diisi dari data live.
const NODES = [
  { name: 'DMS-PGOC-Web-1', label: 'Web-1', tg: 'DMS-PGOC-Web', role: 'nginx + PHP', x: 55 },
  { name: 'DMS-PGOC-Web-2', label: 'Web-2', tg: 'DMS-PGOC-Web', role: 'nginx + PHP', x: 185 },
  { name: 'DMS-PGOC-Core-1', label: 'Core-1', tg: 'DMS-PGOC-Core', role: 'nginx', x: 380 },
  { name: 'DMS-PGOC-Core-2', label: 'Core-2', tg: 'DMS-PGOC-Core', role: 'nginx', x: 510 },
];

const C = {
  gray: { fill: '#f1efe8', stroke: '#888780', t: '#2c2c2a', s: '#5f5e5a' },
  purple: { fill: '#eeedfe', stroke: '#534ab7', t: '#3c3489', s: '#534ab7' },
  teal: { fill: '#e1f5ee', stroke: '#0f6e56', t: '#085041', s: '#0f6e56' },
  red: { fill: '#fcebeb', stroke: '#a32d2d', t: '#791f1f', s: '#a32d2d' },
  amber: { fill: '#faeeda', stroke: '#854f0b', t: '#633806', s: '#854f0b' },
  coral: { fill: '#faece7', stroke: '#993c1d', t: '#712b13', s: '#993c1d' },
};

function Box({ x, y, w, h = 56, c, title, sub, dashed }) {
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx="8" fill={dashed ? 'none' : c.fill} stroke={c.stroke}
        strokeWidth="0.8" strokeDasharray={dashed ? '4 3' : undefined} />
      <text x={x + w / 2} y={y + 20} textAnchor="middle" dominantBaseline="central" fontSize="14" fontWeight="500" fill={c.t}>{title}</text>
      {sub && <text x={x + w / 2} y={y + 39} textAnchor="middle" dominantBaseline="central" fontSize="12" fill={c.s}>{sub}</text>}
    </g>
  );
}

function FlowDiagram({ lb, os }) {
  const targets = {};
  ((lb && lb.groups) || []).forEach((g) => g.targets.forEach((t) => { targets[t.name || t.id] = { ...t, tg: g.name }; }));
  const tgOf = (n) => ((lb && lb.groups) || []).find((g) => g.name === n);
  const tgColor = (n) => { const g = tgOf(n); return !g ? C.gray : g.verdict === 'critical' ? C.red : g.verdict === 'warning' ? C.amber : C.purple; };
  const osC = !os ? C.coral : os.status === 'red' ? C.red : os.status === 'yellow' ? C.amber : C.coral;
  const e5 = lb && lb.metrics && lb.metrics.elb5xx ? lb.metrics.elb5xx.total : null;
  return (
    <svg width="100%" viewBox="0 0 680 640" role="img" style={{ maxWidth: 760 }}>
      <title>dms.pgocid.com request flow</title>
      <defs>
        <marker id="dmsar" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M2 1L8 5L2 9" fill="none" stroke="#888780" strokeWidth="1.5" />
        </marker>
      </defs>
      <Box x={220} y={20} w={240} c={C.gray} title="User (LAN / VPN Velo)" sub="https://dms.pgocid.com" />
      <Box x={190} y={108} w={300} c={C.gray} title="DNS (IDCloudHost)" sub="CNAME → ALB internal" />
      <Box x={150} y={196} w={380} c={lb ? (e5 ? C.amber : C.purple) : C.gray} title={`ALB internal ${LB_NAME}`}
        sub={lb ? `:80→443 · rule host · 5XX ${e5 == null ? '–' : Math.round(e5)} (24 h)` : 'loading…'} />
      <Box x={60} y={292} w={240} c={tgColor('DMS-PGOC-Web')} title="TG DMS-PGOC-Web" sub="host dms · /tg-status.html" />
      <Box x={380} y={292} w={240} c={tgColor('DMS-PGOC-Core')} title="TG DMS-PGOC-Core" sub="host coredms · /status.html" />
      {NODES.map((n) => {
        const t = targets[n.name];
        const c = !t ? C.gray : t.state === 'healthy' ? C.teal : t.state === 'unhealthy' ? C.red : C.amber;
        const ip = t && t.ip ? '.' + t.ip.split('.').pop() + ' ' : '';
        return <Box key={n.name} x={n.x} y={388} w={120} c={c} dashed={!t} title={n.label}
          sub={t ? `${ip}${t.state}` : 'not in TG'} />;
      })}
      <Box x={50} y={532} w={170} c={C.coral} title="RDS MySQL" sub="db.m6g.large :3306" />
      <Box x={255} y={532} w={170} c={osC} title="OpenSearch"
        sub={os ? `${os.status} · ${os.utilisation && os.utilisation.docs != null ? os.utilisation.docs : '–'} docs` : 'VPC endpoint :443'} />
      <Box x={460} y={532} w={170} c={C.coral} title="S3 buckets" sub="data, web, alb-logs" />
      <g stroke="#888780" strokeWidth="1.5" fill="none" markerEnd="url(#dmsar)">
        <path d="M340 76V106" /><path d="M340 164V194" />
        <path d="M260 252V272H180V290" /><path d="M420 252V272H500V290" />
        <path d="M140 348V368H115V386" /><path d="M220 348V368H245V386" />
        <path d="M460 348V368H440V386" /><path d="M540 348V368H570V386" />
        <path d="M135 480V530" /><path d="M340 480V530" /><path d="M545 480V530" />
      </g>
      <path d="M115 444V480M245 444V480M440 444V480M570 444V480M115 480H570" stroke="#888780" strokeWidth="1.5" fill="none" />
    </svg>
  );
}

// Snapshot 2026-11-04 (EC2 DescribeNetworkInterfaces / ELBv2 attributes, akun Indomas).
const IP_MAP = [
  ['10.40.1.22', 'ALB dms-pgoc node (ENI, AZ 3a)', 'Answer of nslookup dms.pgocid.com. Not a server — managed by AWS, IP may change. Does not reply to ping (ICMP), that is normal.'],
  ['10.40.1.44', 'ALB dms-pgoc node (ENI, AZ 3b)', 'Second ALB node. The browser picks either .22 or .44; both forward to the same target groups (cross-zone on).'],
  ['10.40.1.93', 'DMS-PGOC-Web-1 (i-0c4df44f3541a9b09, m6i.xlarge)', 'nginx + PHP-FPM. The node where PHP hangs.'],
  ['10.40.1.74', 'DMS-PGOC-Web-2 (i-09a6cbdb55b778330, m6i.xlarge)', 'nginx + PHP-FPM, partner of Web-1.'],
  ['10.40.1.72 / .82', 'DMS-PGOC-Core-1 / Core-2 (m6i.4xlarge)', 'coredms.pgocid.com backend.'],
];

function Intermittent504() {
  const steps = [
    ['1', 'Browser → DNS', 'dms.pgocid.com is a CNAME to internal-dms-pgoc-…elb.amazonaws.com → 10.40.1.22 or 10.40.1.44 (the two ALB nodes).'],
    ['2', 'ALB → Web target', 'Round robin between Web-1 (.93) and Web-2 (.74). Stickiness is ON (lb_cookie, 24 h): after the first request the browser keeps going to the same Web node for 24 hours.'],
    ['3', 'nginx → PHP-FPM', 'nginx on the Web node passes .php requests to PHP-FPM. If PHP-FPM on Web-1 is stuck (all workers busy/hung), nginx waits until fastcgi_read_timeout and returns "504 Gateway Time-out — nginx" (screenshot 1).'],
    ['4', 'Why ALB does not remove Web-1', 'Health check is GET /tg-status.html (static file, served by nginx only, never touches PHP). nginx is alive → check passes → Web-1 stays "healthy" and keeps getting about half of the users.'],
    ['5', 'Why "sometimes OK"', 'Users/sessions stuck to Web-2 see the normal page (screenshot 2). New browser, incognito or cleared cookie = new coin toss. Restarting PHP-FPM on Web-1 frees the workers, so everyone works again.'],
  ];
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-4 space-y-3">
      <div className="text-sm font-semibold text-gray-800">Why it is sometimes 504, sometimes normal</div>
      <div className="text-xs text-gray-600">A 504 page that says <b>nginx</b> comes from nginx on a Web instance, not from the ALB (an ALB 504 says <code>awselb/2.0</code>). So the ALB reached the server; the server's PHP did not answer.</div>
      <ol className="space-y-1.5">
        {steps.map(([n, t, d]) => (
          <li key={n} className="flex gap-2 text-sm">
            <span className="mt-0.5 h-5 w-5 shrink-0 rounded-full bg-amber-100 text-center text-xs font-semibold leading-5 text-amber-800">{n}</span>
            <span><b className="text-gray-800">{t}.</b> <span className="text-gray-600">{d}</span></span>
          </li>
        ))}
      </ol>
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="w-full text-sm">
          <thead><tr className="bg-gray-50 text-left text-xs text-gray-500">
            <th className="px-3 py-2">IP</th><th className="px-3 py-2">What it is</th><th className="px-3 py-2">Notes</th>
          </tr></thead>
          <tbody>
            {IP_MAP.map(([a, b, c]) => (
              <tr key={a} className="border-t border-gray-100">
                <td className="px-3 py-2 font-mono text-gray-800 whitespace-nowrap">{a}</td>
                <td className="px-3 py-2 text-gray-700">{b}</td>
                <td className="px-3 py-2 text-gray-600">{c}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-xs text-gray-600">
        <b className="text-gray-800">Permanent fix ideas:</b> (a) point the Web TG health check to a small PHP endpoint (e.g. <code>/tg-status.php</code>) so a stuck PHP-FPM makes the node unhealthy and ALB stops sending traffic to it automatically; (b) on Web-1 check PHP-FPM slow log / <code>pm.max_children</code> reached in the FPM log, and set <code>request_terminate_timeout</code> so hung workers are killed; (c) a watchdog that restarts php-fpm when the PHP endpoint stops responding.
      </div>
      <div className="text-[11px] text-gray-400">IP/attribute snapshot 2026-11-04. ALB idle timeout 1800 s, cross-zone ON, deregistration delay Web 120 s.</div>
    </div>
  );
}

function FlowTab() {
  const [lb, setLb] = useState(null);
  const [os, setOs] = useState(null);
  const [err, setErr] = useState(null);
  const [at, setAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback((refresh) => {
    setLoading(true); setErr(null);
    const r = refresh ? '&refresh=1' : '';
    Promise.all([
      api.get(`/api/ec2-health/alb?account=${ACCOUNT}&hours=24${r}`),
      api.get(`/api/ec2-health/opensearch?account=${ACCOUNT}&hours=24${r}`),
    ]).then(([a, o]) => {
      setLb(((a.data && a.data.loadBalancers) || []).find((x) => x.name === LB_NAME) || null);
      setOs(((o.data && o.data.domains) || []).find((x) => x.name === OS_DOMAIN) || null);
      setAt(new Date());
    }).catch((e) => setErr((e.response && e.response.data && e.response.data.error) || e.message))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(false); }, [load]);

  const rows = [
    ['DNS', 'dms.pgocid.com / coredms.pgocid.com → CNAME to internal ALB (reachable only from LAN/VPN/peering)', 'DNS resolve, VPN Velo, routing 10.40.1.x'],
    ['ALB', 'TLS *.pgocid.com (ACM), :80 redirect to :443, routing by host header', 'ALB 5XX, TargetResponseTime, logs in s3://dms-pgoc-alb-logs'],
    ['Web', 'nginx + PHP (CodeIgniter), 2 instances in TG DMS-PGOC-Web', 'Target health, target:port column in ALB log'],
    ['Core', 'nginx, 2 instances in TG DMS-PGOC-Core (coredms)', 'Target health, CPU instance'],
    ['RDS', 'MySQL — metadata, user, approval', 'CPU, connections, read/write latency'],
    ['OpenSearch', 'Full-text search of document content (planned)', 'OpenSearch tab: status, JVM, documents, latency'],
    ['S3', 'Document files (dms-pgoc-data), web assets, ALB logs', 'CloudTrail / S3 metrics'],
  ];
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
        <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: C.teal.fill, border: `1px solid ${C.teal.stroke}` }} /> healthy</span>
        <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: C.amber.fill, border: `1px solid ${C.amber.stroke}` }} /> warning / draining</span>
        <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: C.red.fill, border: `1px solid ${C.red.stroke}` }} /> unhealthy / red</span>
        <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm border border-dashed border-gray-400" /> not registered in TG</span>
        <div className="ml-auto flex items-center gap-2">
          {at && <span>Updated {at.toLocaleTimeString()}</span>}
          <button type="button" onClick={() => load(true)} disabled={loading}
            className="rounded-md border border-gray-200 bg-white px-2.5 py-1 font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            {loading ? 'Loading…' : 'Refresh'}</button>
        </div>
      </div>
      {err && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <FlowDiagram lb={lb} os={os} />
      </div>
      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <table className="w-full text-sm">
          <thead><tr className="bg-gray-50 text-left text-xs text-gray-500">
            <th className="px-3 py-2">Layer</th><th className="px-3 py-2">Function</th><th className="px-3 py-2">Check when troubleshooting</th>
          </tr></thead>
          <tbody>
            {rows.map(([a, b, c]) => (
              <tr key={a} className="border-t border-gray-100">
                <td className="px-3 py-2 font-medium text-gray-800">{a}</td>
                <td className="px-3 py-2 text-gray-600">{b}</td>
                <td className="px-3 py-2 text-gray-600">{c}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Intermittent504 />
      <div className="text-[11px] leading-relaxed text-gray-400">
        Read-only from AWS (ELBv2, OpenSearch, CloudWatch), Indomas account. The project is managed by the DMS team — this page is for monitoring and troubleshooting only.
      </div>
    </div>
  );
}

export default function DmsProject() {
  const [tab, setTab] = useState(() => { try { return localStorage.getItem('dms_project_tab') || 'flow'; } catch (_) { return 'flow'; } });
  const pick = (k) => { setTab(k); try { localStorage.setItem('dms_project_tab', k); } catch (_) {} };
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">dms.pgocid.com · AWS Indomas (ap-southeast-3) · read-only</p>
      <div className="inline-flex rounded-lg bg-slate-100/80 p-0.5 text-sm">
        {TABS.map(([k, l]) => (
          <button key={k} type="button" onClick={() => pick(k)}
            className={`rounded-md px-3 py-1 font-medium ${tab === k ? 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>{l}</button>
        ))}
      </div>
      {tab === 'flow' && <FlowTab />}
      {tab === 'alb' && <Ec2AlbView account={ACCOUNT} />}
      {tab === 'opensearch' && <Ec2OpenSearchView account={ACCOUNT} />}
      {tab === 'about' && <AboutTab />}
    </div>
  );
}
